import { ActionType } from 'deox';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { Api, Auth } from 'services/api';

import {
  register,
  login,
  getStripePrices,
  nextRegisterStep,
  confirmEmail,
  signupSubClinic,
  forgotPassword,
  createNewPassword,
} from './actions';
import { RootState } from 'types';
import { processRequestError } from 'modules/error/actions';
import { getUserInfo, setCurrency } from 'modules/user/actions';
import { setNotification } from 'modules/app/actions';
import { t } from 'utils';

import texts from './texts';

function* getStripePricesSaga() {
  try {
    const { currencyType } = yield select((state: RootState) => state.user);
    const { temporary } = yield select((state: RootState) => state.auth);
    const form = {
      currencyType,
      temporary,
    }
    const { data } = yield call(Auth.getStripePrices, form);
    yield put(setCurrency(data[0].currency));
    yield put(getStripePrices.success(data));
  } catch (error) {
    yield put(getStripePrices.fail(error));
  }
}

function* registerSaga({ payload }: ActionType<typeof register.request>): SagaIterator {
  try {
    const { registerData, itSystem } = yield select((state: RootState) => state.auth);
    const { countryCode } = yield select((state: RootState) => state.user);
    const body: any = {};
    body.full_name = registerData.fullName;
    body.email = registerData.email;
    body.password = registerData.password;
    body.position_title = registerData.yourPosition;
    body.clinic_name = registerData.clinicName;
    body.number_of_machines = registerData.currentStripePrice.number_of_machines;
    body.phone_number = registerData.phoneNumber;
    body.country_code=  countryCode;
    body.domain = window.location.origin;
    body.country = registerData.country;
    
    let data;
    if (payload) {
      body.connected_to_et = itSystem;
      body.number_of_clinics = registerData.howManyClinics;
      data = yield call(Auth.registerCompany, body);
    } else {
      if (registerData.billingType) {
        body.billing_type = String(registerData.billingType).toLowerCase();
      } else {
        body.billing_type = String(registerData.currentStripePrice.billing_type).toLowerCase();
      }
      data = yield call(Auth.registerClinic, body);
    }
    yield put(nextRegisterStep());
    yield put(register.success(data));
  } catch (error) {
    yield put(processRequestError({ error, failAction: register.fail }));
  }
}

function* loginSaga() {
  try {
    const { registerData } = yield select((state: RootState) => state.auth);
    const body: any = {
      email: registerData.email,
      password: registerData.password,
    };

    const { data } = yield call(Auth.login, body);
    Api.setAuthToken(data.access);
    yield put(getUserInfo.request());
    yield put(login.success(data));
  } catch (error) {
    yield put(processRequestError({ error, failAction: login.fail }));
  }
}

function* confirmEmailSaga({ payload }: ActionType<typeof confirmEmail.request>): SagaIterator {
  try {
    yield call(Auth.confirmEmail, payload);
    yield put(confirmEmail.success());
  } catch (error) {
    yield put(processRequestError({ error, failAction: confirmEmail.fail }));
  }
}

function* signupSubClinicSaga() {
  try {
    const { clinicRegisterData } = yield select((state: RootState) => state.auth);

    const body: any = {
      enterprise_token: clinicRegisterData.enterpriseToken,
      full_name: clinicRegisterData.fullName,
      phone_number: clinicRegisterData.mobileNumber,
      store_location: clinicRegisterData.storeLocation,
      store_number: clinicRegisterData.storePhone,
      email: clinicRegisterData.email,
      password: clinicRegisterData.password,
      clinic_name: clinicRegisterData.companyName,
      country: clinicRegisterData.country,
    };

    yield call(Auth.signupSubClinic, body);

    yield put(signupSubClinic.success());
  } catch (error) {
    yield put(processRequestError({ error, failAction: signupSubClinic.fail }));
  }
}

function* forgotPasswordSaga({ payload }: ActionType<typeof forgotPassword.request>): SagaIterator {
  try {
    yield call(Auth.forgotPassword, payload);
    // @ts-ignore
    yield put(setNotification(true, t(texts.auth.linkSent)));
    yield put(forgotPassword.success());
  } catch (error) {
    yield put(processRequestError({ error, failAction: forgotPassword.fail }));
  }
}

function* createNewPasswordSaga({
  payload,
}: ActionType<typeof createNewPassword.request>): SagaIterator {
  const { token, password, confirmPassword } = payload;
  try {
    yield call(Auth.createNewPassword, token, password, confirmPassword);
    // @ts-ignore
    yield put(setNotification(true, t(texts.auth.passwordReset)));
    yield put(createNewPassword.success());
  } catch (error) {
    yield put(processRequestError({ error, failAction: createNewPassword.fail }));
  }
}

export function* watchAuth(): SagaIterator {
  yield takeLatest(getStripePrices.request, getStripePricesSaga);
  yield takeLatest(register.request, registerSaga);
  yield takeLatest(login.request, loginSaga);
  yield takeLatest(confirmEmail.request, confirmEmailSaga);
  yield takeLatest(signupSubClinic.request, signupSubClinicSaga);
  yield takeLatest(forgotPassword.request, forgotPasswordSaga);
  yield takeLatest(createNewPassword.request, createNewPasswordSaga);
}
